// Tasks API service
import { baseClient } from './baseClient';
import type { Task, TaskCreate, TaskUpdate, TaskFilters } from '../types/api';

export class TasksApi {
  private static instance: TasksApi;

  private constructor() { }

  public static getInstance(): TasksApi {
    if (!TasksApi.instance) {
      TasksApi.instance = new TasksApi();
    }
    return TasksApi.instance;
  }

  /**
   * Get all tasks with optional filtering
   */
  async getTasks(filters?: TaskFilters): Promise<Task[]> {
    const params = new URLSearchParams();

    if (filters?.q) params.append('q', filters.q);
    if (filters?.skip) params.append('skip', filters.skip.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.deal_id) params.append('deal_id', filters.deal_id);
    if (filters?.is_completed !== undefined) params.append('is_completed', filters.is_completed.toString());

    const queryString = params.toString();
    const url = queryString ? `/tasks?${queryString}` : '/tasks';

    return baseClient.get<Task[]>(url);
  }

  /**
   * Get a single task by ID
   */
  async getTask(id: string): Promise<Task> {
    return baseClient.get<Task>(`/tasks/${id}`);
  }

  /**
   * Create a new task
   */
  async createTask(task: TaskCreate): Promise<Task> {
    return baseClient.post<Task>('/tasks', task);
  }

  /**
   * Update an existing task
   */
  async updateTask(id: string, task: TaskUpdate): Promise<Task> {
    return baseClient.patch<Task>(`/tasks/${id}`, task);
  }

  /**
   * Delete a task
   */
  async deleteTask(id: string): Promise<void> {
    return baseClient.delete<void>(`/tasks/${id}`);
  }

  /**
   * Mark a task as completed
   */
  async completeTask(id: string): Promise<Task> {
    return this.updateTask(id, { is_completed: true });
  }

  /**
   * Mark a task as incomplete
   */
  async uncompleteTask(id: string): Promise<Task> {
    return this.updateTask(id, { is_completed: false });
  }

  /**
   * Get tasks for a specific deal
   */
  async getTasksByDeal(dealId: string): Promise<Task[]> {
    return this.getTasks({ deal_id: dealId });
  }

  /**
   * Get completed tasks
   */
  async getCompletedTasks(): Promise<Task[]> {
    return this.getTasks({ is_completed: true });
  }

  /**
   * Get incomplete tasks
   */
  async getIncompleteTasks(): Promise<Task[]> {
    return this.getTasks({ is_completed: false });
  }

  /**
   * Get tasks due today
   */
  async getTasksDueToday(): Promise<Task[]> {
    const today = new Date().toISOString().split('T')[0];
    const tasks = await this.getTasks();
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = new Date(task.due_date).toISOString().split('T')[0];
      return taskDate === today;
    });
  }

  /**
   * Get overdue tasks (using backend endpoint)
   */
  async getOverdueTasks(): Promise<Task[]> {
    return baseClient.get<Task[]>('/tasks/overdue');
  }

  /**
   * Get tasks assigned to current user
   */
  async getMyTasks(status?: string): Promise<Task[]> {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const queryString = params.toString();
    const url = queryString ? `/tasks/my?${queryString}` : '/tasks/my';
    return baseClient.get<Task[]>(url);
  }

  /**
   * Assign a task to a user
   */
  async assignTask(taskId: string, assignedToId: number): Promise<{ message: string }> {
    return baseClient.post<{ message: string }>(`/tasks/${taskId}/assign`, { assigned_to_id: assignedToId });
  }

  /**
   * Get tasks by priority
   */
  async getTasksByPriority(priority: 'low' | 'medium' | 'high' | 'urgent'): Promise<Task[]> {
    return this.getTasks({ priority });
  }

  /**
   * Get tasks by status
   */
  async getTasksByStatus(status: 'pending' | 'in_progress' | 'completed' | 'cancelled'): Promise<Task[]> {
    return this.getTasks({ status });
  }
}

// Export singleton instance
export const tasksApi = TasksApi.getInstance(); 